import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { supabase } from "@/services/supabase";
import { CLOUD_RUN_URL } from "@/services/shared";
import { useAuth } from "./AuthContext";

interface MicrosoftAuthContextType {
  hasUserMicrosoftAuth: boolean | null;
  isCheckingMicrosoftAuth: boolean;
  checkMicrosoftAuth: () => Promise<void>;
  lastChecked: Date | null;
}

const MicrosoftAuthContext = createContext<MicrosoftAuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useMicrosoftAuth = () => {
  const context = useContext(MicrosoftAuthContext);
  if (context === undefined) {
    throw new Error("useMicrosoftAuth must be used within a MicrosoftAuthProvider");
  }
  return context;
};

interface MicrosoftAuthProviderProps {
  children: ReactNode;
}

export const MicrosoftAuthProvider: React.FC<MicrosoftAuthProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [hasUserMicrosoftAuth, setHasUserMicrosoftAuth] = useState<boolean | null>(null);
  const [isCheckingMicrosoftAuth, setIsCheckingMicrosoftAuth] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const checkMicrosoftAuth = useCallback(async () => {
    if (!user?.id) {
      setHasUserMicrosoftAuth(false);
      setLastChecked(new Date());
      return;
    }

    setIsCheckingMicrosoftAuth(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        setHasUserMicrosoftAuth(false);
        setLastChecked(new Date());
        return;
      }

      // Try to make a simple request to test if user has valid Microsoft tokens
      const response = await fetch(`${CLOUD_RUN_URL}/api/sharepoint/status?cb=${Date.now()}`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      // If we get a 401 or 403, user needs to authenticate with Microsoft
      if (response.status === 401 || response.status === 403) {
        setHasUserMicrosoftAuth(false);
      } else if (response.ok) {
        setHasUserMicrosoftAuth(true);
      } else {
        // Other errors might be network issues, assume they need auth
        setHasUserMicrosoftAuth(false);
      }

      setLastChecked(new Date());
    } catch (error) {
      console.error("Error checking Microsoft auth:", error);
      setHasUserMicrosoftAuth(false);
      setLastChecked(new Date());
    } finally {
      setIsCheckingMicrosoftAuth(false);
    }
  }, [user?.id]);

  // Check authentication when user changes
  useEffect(() => {
    if (user?.id) {
      checkMicrosoftAuth();
    } else {
      setHasUserMicrosoftAuth(null);
      setLastChecked(null);
    }
  }, [user?.id, checkMicrosoftAuth]);

  // Re-check authentication every 5 minutes to handle token expiration
  useEffect(() => {
    if (!user?.id) return;

    const interval = setInterval(
      () => {
        checkMicrosoftAuth();
      },
      5 * 60 * 1000,
    ); // 5 minutes

    return () => clearInterval(interval);
  }, [user?.id, checkMicrosoftAuth]);

  // Listen for changes in the URL that might indicate successful OAuth
  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("sharepoint") === "connected" && user?.id) {
        // Re-check auth after a brief delay to allow token processing
        setTimeout(() => {
          checkMicrosoftAuth();
        }, 2000);
      }
    };

    // Check on mount and when the URL changes
    handleUrlChange();
    window.addEventListener("popstate", handleUrlChange);

    return () => window.removeEventListener("popstate", handleUrlChange);
  }, [user?.id, checkMicrosoftAuth]);

  const value: MicrosoftAuthContextType = {
    hasUserMicrosoftAuth,
    isCheckingMicrosoftAuth,
    checkMicrosoftAuth,
    lastChecked,
  };

  return <MicrosoftAuthContext.Provider value={value}>{children}</MicrosoftAuthContext.Provider>;
};
